import { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CheckCircle, Hourglass, FloppyDisk, PencilSimple, X, LinkSimple, Info, ArrowRight, MagnifyingGlass, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import IntimationDialog from '@/components/IntimationDialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// Canonical workflow stages → label + tailwind color. Single source of
// truth for the dropdown + the row-level color cue. Order matters — the
// dropdown renders top-down in this sequence.
const WORK_STAGES = [
  { value: 'pending',       label: 'Pending',       color: 'text-[#71717A]' },
  { value: 'in_process',    label: 'Preparing',     color: 'text-[#EA580C]' },
  { value: 'submitted',     label: 'Submitted',     color: 'text-[#F59E0B]' },
  { value: 'under_process', label: 'Under Process', color: 'text-[#3B82F6]' },
  // Resubmission — application was sent back by the issuing authority and
  // needs to be corrected & re-filed. Placed right after Under Process so
  // staff see it as a natural side-branch of the in-flight stage.
  { value: 'resubmission',  label: 'Resubmission',  color: 'text-[#8B5CF6]' },
  { value: 'docs_pending',  label: 'Docs. Pending', color: 'text-[#EF4444]' },
  { value: 'approved',      label: 'Approved',      color: 'text-[#10B981]' },
  { value: 'delivered',     label: 'Delivered',     color: 'text-[#16A34A]' },
];
const stageMeta = (raw) => {
  const v = (raw || '').toLowerCase();
  // Only `completed` is now treated as legacy — it was always labelled
  // "Submitted" in the previous UI. `pending` and `in_process` are now
  // first-class values exposed at the top of the dropdown.
  const legacy = { completed: 'submitted' };
  const canonical = legacy[v] || v || 'pending';
  return WORK_STAGES.find(s => s.value === canonical) || WORK_STAGES[0];
};

export default function ProcessingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase D — Work-status filter. `__all__` shows every row EXCEPT delivered
  // (those live under their own bucket); any other value narrows the list
  // to that specific workflow stage.
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);
  const [editingRemarks, setEditingRemarks] = useState({});
  // Intimation popup state. `intimation.row` is the /processing row that
  // just flipped to Submitted/Approved; `intimation.status` is which
  // template to use; `templates` holds the editable strings from company
  // settings (fetched once on mount).
  const [intimation, setIntimation] = useState({ row: null, status: '' });
  // Full-page invoice editor triggered by clicking an invoice number in
  // the queue. Holds only the id — the actual UI is rendered by embedding
  // the real EditInvoicePage inside an iframe (see the Dialog below), so
  // the operator gets the *exact* same edit experience they'd get on
  // /invoices/edit/{id} without leaving /processing.
  const [invoicePreview, setInvoicePreview] = useState(null);

  const openInvoicePreview = (invoiceId) => {
    if (!invoiceId) return;
    setInvoicePreview({ id: invoiceId });
  };
  const [templates, setTemplates] = useState({});

  const fetchProcessing = useCallback(() => {
    api.get('/processing').then(r => setRows(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProcessing(); }, [fetchProcessing]);
  useEffect(() => { if (!loading) searchRef.current?.focus(); }, [loading]);
  useEffect(() => {
    api.get('/settings/company')
      .then(r => setTemplates(r.data?.intimation_templates || {}))
      .catch(() => {});
  }, []);

  const rowKey = (row) => `${row.invoice_id}-${row.item_index}`;

  const handleSaveRemarks = async (row) => {
    const k = rowKey(row);
    const newRemarks = editingRemarks[k];
    if (newRemarks === undefined) return;
    try {
      await api.put(`/invoices/${row.invoice_id}/items/${row.item_index}/remark`, { remark: newRemarks });
      toast.success('Remarks updated');
      setEditingRemarks(prev => { const n = { ...prev }; delete n[k]; return n; });
      fetchProcessing();
    } catch { toast.error('Failed to update remarks'); }
  };

  const handleWorkStatus = async (row, newStatus) => {
    try {
      await api.put(`/invoices/${row.invoice_id}/items/${row.item_index}/work-status`, { work_status: newStatus });
      const label = WORK_STAGES.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(
        newStatus === 'delivered'
          ? `${row.service_name} delivered — removed from queue`
          : `${row.service_name} → ${label}`
      );
      fetchProcessing();
    } catch {
      toast.error('Failed to update work status');
    }
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  // Filter behavior:
  //   - `__all__`  → every stage EXCEPT delivered (delivered rows only
  //                   surface under their own explicit filter so the busy
  //                   default view stays focused on what still needs work)
  //   - `<stage>`   → only rows currently on that specific stage
  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const matchesSearch = (r) => {
    if (!q) return true;
    const name = `${r.customer_name || ''} ${r.applicant_name || ''}`.toLowerCase();
    const phone = String(r.customer_phone || '').replace(/\D/g, '');
    return name.includes(q) || (qDigits.length > 0 && phone.includes(qDigits));
  };
  const visibleRows = (statusFilter === '__all__'
    ? rows.filter(r => stageMeta(r.work_status).value !== 'delivered')
    : rows.filter(r => stageMeta(r.work_status).value === statusFilter)
  ).filter(matchesSearch);
  const counts = rows.reduce((acc, r) => {
    const v = stageMeta(r.work_status).value;
    acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});
  // Total for the top count badge — mirrors visible-vs-total when filtering
  // and drops delivered from the denominator so the "N pending" label
  // stays honest under the default view.
  const totalNonDelivered = rows.length - (counts['delivered'] || 0);

  // Human-friendly date+time formatter used by the Last Update column and
  // the hover-card history rows. Always renders IST (matches the rest of
  // the app which formats dates via `en-IN`).
  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return iso; }
  };
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('en-IN'); } catch { return iso; }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="processing-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer name or mobile…"
              className="h-9 w-64 pl-8 pr-8 text-sm rounded-lg border-[#E4E4E7]"
              data-testid="followup-search-input"
            />
            {search && (
              <button type="button" onClick={() => { setSearch(''); searchRef.current?.focus(); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#0A0A0A]" data-testid="followup-search-clear" title="Clear search">
                <X size={12} />
              </button>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Work Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-lg w-56 h-9 text-sm" data-testid="followup-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" className="text-xs">All ({totalNonDelivered})</SelectItem>
              {WORK_STAGES.map(s => (
                <SelectItem key={s.value} value={s.value} className={`text-xs ${s.color}`} data-testid={`followup-status-opt-${s.value}`}>
                  {s.label} ({counts[s.value] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {statusFilter !== '__all__' && (
            <Button variant="ghost" size="sm" className="text-xs text-[#52525B] h-9" onClick={() => setStatusFilter('__all__')} data-testid="followup-clear-filter">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
        <Badge className="rounded-lg text-sm px-3 py-1 bg-[#F59E0B]/10 text-[#D97706] border-[#F59E0B]/20" variant="outline">
          <Hourglass size={14} className="mr-1.5" /> {visibleRows.length}{statusFilter === '__all__' ? '' : ` / ${totalNonDelivered}`} pending
        </Badge>
      </div>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {visibleRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <CheckCircle size={48} className="text-[#10B981]/30 mb-3" />
              <p className="text-sm text-[#52525B]">
                {statusFilter === '__all__' ? 'All follow-up items have been processed' : 'No items in this status'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Invoice</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Service</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Payment</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Work Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Remarks</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">By</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Last Update</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map(row => {
                  const stage = stageMeta(row.work_status);
                  const k = rowKey(row);
                  const isEditing = editingRemarks[k] !== undefined;
                  const remarksValue = isEditing ? editingRemarks[k] : (row.remark || '');
                  const isDelivered = stage.value === 'delivered';
                  // Delivered rows are shown greyed out — the operator has
                  // filtered explicitly to see them, so keep the row
                  // legible but visually de-emphasised.
                  const rowClass = `border-[#E4E4E7] ${isDelivered ? 'bg-[#FAFAFA] text-[#A1A1AA]' : ''}`;
                  const history = Array.isArray(row.work_status_history) ? row.work_status_history : [];
                  const lastUpdateIso = row.last_updated_at || (history.length > 0 ? history[history.length - 1].changed_at : '');
                  return (
                    <TableRow key={k} className={rowClass} data-testid={`followup-row-${k}`}>
                      <TableCell className={`text-xs ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`} data-testid={`followup-date-${k}`}>
                        {fmtDate(row.created_at)}
                      </TableCell>
                      <TableCell className={`text-xs font-mono ${isDelivered ? 'text-[#93C5FD]' : 'text-[#3B82F6]'}`}>
                        <button
                          type="button"
                          onClick={() => openInvoicePreview(row.invoice_id)}
                          className={`hover:underline cursor-pointer font-mono ${isDelivered ? 'text-[#93C5FD]' : 'text-[#3B82F6] hover:text-[#2563EB]'}`}
                          title={`Preview ${row.invoice_number}`}
                          data-testid={`followup-invoice-link-${k}`}
                        >
                          {row.invoice_number}
                        </button>
                      </TableCell>
                      <TableCell className={`text-xs font-medium ${isDelivered ? 'text-[#A1A1AA]' : ''}`} data-testid={`followup-customer-${k}`}>
                        <div className="leading-tight">{row.customer_name}</div>
                        {/* Applicant chip — only when different from the
                            billed customer. Keeps the follow-up screen clear
                            about who the service is actually being done for
                            (family member / delegate / govt applicant). */}
                        {(() => {
                          const appl = (row.applicant_name || '').trim();
                          const cust = (row.customer_name || '').trim();
                          if (!appl || appl.toLowerCase() === cust.toLowerCase()) return null;
                          return (
                            <div
                              className={`text-[10px] truncate ${isDelivered ? 'text-[#C4B5FD]' : 'text-[#7C3AED]'}`}
                              title={`Applicant: ${appl}`}
                              data-testid={`followup-applicant-${k}`}
                            >
                              ({appl})
                            </div>
                          );
                        })()}
                        {/* Mobile under name — quick visual cue so staff
                            can dial without opening the customer card. */}
                        {row.customer_phone && (
                          <a
                            href={`tel:${row.customer_phone}`}
                            className={`text-[10px] font-mono hover:underline tracking-wide ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#52525B] hover:text-[#3B82F6]'}`}
                            data-testid={`followup-mobile-${k}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.customer_phone}
                          </a>
                        )}
                      </TableCell>
                      <TableCell className={`text-xs ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`}>{row.service_name}{row.quantity > 1 ? ` × ${row.quantity}` : ''}</TableCell>
                      <TableCell className={`text-xs text-right font-mono ${isDelivered ? 'text-[#A1A1AA]' : ''}`}>{fmt(row.total)}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-md text-[10px] ${isDelivered ? 'opacity-60' : ''} ${row.status === 'paid' ? 'bg-[#D1FAE5] text-[#10B981]' : row.status === 'partial' ? 'bg-[#FFFBEB] text-[#D97706]' : 'bg-[#FEE2E2] text-[#EF4444]'}`} variant="outline">
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Select value={stage.value} onValueChange={(v) => handleWorkStatus(row, v)}>
                            <SelectTrigger
                              className={`h-7 w-[140px] text-xs rounded-md border-[#E4E4E7] ${stage.color} font-semibold`}
                              data-testid={`work-status-trigger-${k}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WORK_STAGES.map(s => (
                                <SelectItem key={s.value} value={s.value} className={`text-xs ${s.color}`} data-testid={`work-status-opt-${s.value}-${k}`}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => setIntimation({ row, status: stage.value })}
                            title={`Send "${stage.label}" status on WhatsApp`}
                            data-testid={`send-status-whatsapp-${k}`}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0 ${isDelivered ? 'bg-[#F4F4F5] text-[#A1A1AA]' : 'bg-[#DCFCE7] text-[#16A34A] hover:bg-[#BBF7D0]'}`}
                          >
                            <WhatsappLogo size={13} weight="fill" />
                          </button>
                          {row.service_link2 ? (
                            <a
                              href={row.service_link2}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Open ${row.service_name} follow-up portal`}
                              data-testid={`item-link2-${k}`}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0 ${isDelivered ? 'bg-[#F4F4F5] text-[#A1A1AA]' : 'bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD]'}`}
                            >
                              <LinkSimple size={12} weight="bold" />
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[180px]">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={remarksValue}
                              onChange={e => setEditingRemarks({ ...editingRemarks, [k]: e.target.value })}
                              className="h-7 text-xs rounded-md border-[#E4E4E7]"
                              placeholder="Add remarks..."
                              autoFocus
                              data-testid={`remarks-input-${k}`}
                            />
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#10B981] hover:text-[#059669]"
                              onClick={() => handleSaveRemarks(row)} data-testid={`save-remarks-${k}`} title="Save">
                              <FloppyDisk size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#52525B] hover:text-[#EF4444]"
                              onClick={() => setEditingRemarks(prev => { const n = { ...prev }; delete n[k]; return n; })}
                              data-testid={`cancel-remarks-${k}`} title="Cancel">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 group">
                            <span className={`text-xs flex-1 truncate ${row.remark ? (isDelivered ? 'text-[#71717A]' : 'text-[#0A0A0A]') : 'text-[#A1A1AA] italic'}`} title={row.remark || ''}>
                              {row.remark || 'No remark'}
                            </span>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#52525B] hover:text-[#3B82F6] opacity-60 hover:opacity-100"
                              onClick={() => setEditingRemarks({ ...editingRemarks, [k]: row.remark || '' })}
                              data-testid={`edit-remarks-${k}`} title="Edit remark">
                              <PencilSimple size={13} />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={`text-xs ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`}>{row.created_by_name || '-'}</TableCell>
                      <TableCell className={`text-xs align-top ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#52525B]'}`} data-testid={`followup-last-update-${k}`}>
                        {lastUpdateIso ? (
                          <div className="leading-tight">
                            <div className="flex items-baseline gap-0.5">
                              <span className="font-mono">{fmtDateTime(lastUpdateIso)}</span>
                              {history.length > 0 && (
                                <HoverCard openDelay={80} closeDelay={120}>
                                  <HoverCardTrigger asChild>
                                    <sup
                                      className="text-[#3B82F6] cursor-help inline-flex"
                                      data-testid={`last-update-info-${k}`}
                                    >
                                      <Info size={10} weight="fill" />
                                    </sup>
                                  </HoverCardTrigger>
                                  <HoverCardContent
                                    align="end"
                                    className="w-80 p-3 rounded-lg border-[#E4E4E7] shadow-md"
                                    data-testid={`last-update-popover-${k}`}
                                  >
                                    <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] mb-2">
                                      Status History ({history.length})
                                    </p>
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                      {[...history].reverse().map((h, i) => {
                                        const fromLabel = WORK_STAGES.find(s => s.value === h.from)?.label || (h.from || 'Pending');
                                        const toLabel = WORK_STAGES.find(s => s.value === h.to)?.label || h.to;
                                        return (
                                          <div key={i} className="text-[11px] leading-snug border-l-2 border-[#E4E4E7] pl-2">
                                            <p className="font-semibold text-[#0A0A0A]">
                                              {h.changed_by_name || 'Unknown user'}
                                            </p>
                                            <p className="text-[#52525B] font-mono text-[10px]">{fmtDateTime(h.changed_at)}</p>
                                            <p className="flex items-center gap-1 mt-0.5">
                                              <span className="text-[#71717A]">{fromLabel}</span>
                                              <ArrowRight size={10} className="text-[#A1A1AA]" />
                                              <span className={WORK_STAGES.find(s => s.value === h.to)?.color || 'text-[#0A0A0A]'}>{toLabel}</span>
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              )}
                            </div>
                            {row.last_updated_by_name && (
                              <p className={`text-[10px] italic ${isDelivered ? 'text-[#A1A1AA]' : 'text-[#71717A]'}`}>
                                by {row.last_updated_by_name}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="italic text-[#A1A1AA]">Never updated</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Full-page invoice editor — triggered by clicking an invoice
          number in the queue. Renders the ACTUAL EditInvoicePage inside
          an iframe so the operator gets the full editor UX (line items,
          payment mode, applicant, notes, etc.) without leaving the queue.
          Closing (X / Esc / backdrop) refetches the queue so any saves
          from inside the iframe surface immediately. */}
      <Dialog open={!!invoicePreview} onOpenChange={(v) => { if (!v) { setInvoicePreview(null); fetchProcessing(); } }}>
        <DialogContent
          className="max-w-none w-screen h-screen p-0 rounded-none border-0 sm:rounded-none"
          data-testid="followup-invoice-preview"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Invoice {invoicePreview?.invoice_number || ''}</DialogTitle>
            <DialogDescription>Full invoice editor</DialogDescription>
          </DialogHeader>
          {invoicePreview?.id ? (
            <iframe
              key={invoicePreview.id}
              src={`/invoices/edit/${invoicePreview.id}`}
              title="Invoice editor"
              className="w-full h-full border-0"
              data-testid="followup-invoice-iframe"
              onLoad={(e) => {
                // After the EditInvoicePage's Save/Delete redirects away
                // from `/invoices/edit/{id}` (usually to `/invoices`), auto-
                // close the popup and refresh the follow-up queue so any
                // status / item changes surface right away. Wrapped in
                // try/catch because same-origin iframe access can still
                // throw during in-flight navigations.
                try {
                  const path = e.target.contentWindow?.location?.pathname || '';
                  if (path && !path.startsWith(`/invoices/edit/${invoicePreview.id}`)) {
                    setInvoicePreview(null);
                    fetchProcessing();
                  }
                } catch { /* cross-origin during nav — ignore */ }
              }}
            />
          ) : (
            <div className="p-8 text-center text-sm text-[#A1A1AA]">Loading invoice…</div>
          )}
        </DialogContent>
      </Dialog>

      <IntimationDialog
        open={!!intimation.row}
        onOpenChange={(v) => { if (!v) setIntimation({ row: null, status: '' }); }}
        row={intimation.row}
        status={intimation.status}
        statusLabel={WORK_STAGES.find(s => s.value === intimation.status)?.label || intimation.status}
        template={templates[intimation.status] || ''}
      />
    </div>
  );
}
